import React from 'react';

// Playbook marker renderer (034, resized Formation-Editor-cleanup pass) —
// PURE presentation, given already-computed screen coordinates (never
// document x/y directly, never pixels persisted anywhere). Forwards raw
// pointer events to whichever caller composed it (the editor) rather
// than containing any drag/selection logic itself — keeps this reusable
// by a future read-only Film Study view with zero editing behavior
// attached.
//
// Visual size is deliberately DECOUPLED from touch/pointer hit-target
// size: VISUAL_RADIUS is a compact position dot (so widening the field's
// CSS footprint on desktop/tablet doesn't inflate players into "five-
// yard-wide" blobs), while HIT_RADIUS is a larger, fully transparent
// circle underneath that actually owns the pointer handlers — the
// standard "small visible marker + larger invisible hit area" pattern,
// so mouse/touch/pen targeting stays comfortable even though the drawn
// dot stays small. Both are plain viewBox units, same coordinate space
// as everything else here.
// VISUAL_RADIUS is exported so the Formation Editor's drag-collision
// guard (TeamPlaybookPage.js) derives its minimum marker separation from
// the SAME radius this component actually draws, rather than a second,
// possibly-drifting magic number.
//
// Phase B.3d — MARKER SCALE CORRECTION. The ±20yd field-framing widening
// (B.3b) made the pre-existing VISUAL_RADIUS=9 read as an oversized
// ~2-3-yard-wide token against the new, more zoomed-out field. Shrunk to
// 6.75 — exactly 75% of the old visual diameter, at the conservative
// (more-readable) end of the requested 70-75% band. HIT_RADIUS is
// UNCHANGED at 20: this pass's entire point is a SMALLER visual token
// with the SAME comfortable touch target, and the two were already
// architecturally decoupled (this exact header note, unchanged since
// the Formation-Editor-cleanup pass) — only the value of one of them
// moved, not the separation between them. Every other place in the
// codebase that cares about the visible marker boundary (Block contact-
// point pullback, Man-line endpoint pullback, the Zone "run here" arrow,
// the Formation-editor collision guard, the LOS onside/Center boundary
// clamp) reads this SAME exported constant rather than a hardcoded
// copy, so all of them automatically pick up the new smaller boundary
// with zero further code changes — see TeamPlaybookPlayEditor.js's own
// blockContactPoint()/manConnectionScreenPoints()/zoneArrowScreenPoints(),
// utils/playbookMarkers.js's MIN_MARKER_SEPARATION, and
// fieldCoordinates.js's visualRadiusYards().
export const VISUAL_RADIUS = 6.75;
const HIT_RADIUS = 20;

// Phase B.3d (§8/§9) — the SELECTION RING is now a genuinely separate
// concentric element (a real gap from the token, not the token's own
// border merely recoloring) so a selected player reads as "a thin ring
// tight around the player," never as a preview of the future Threat
// halo. Deliberately kept well inside the OLD (pre-B.3d) visual
// footprint (radius 9) — outer edge of the ring lands at roughly 8.9,
// leaving the region from there out to ~9 and beyond genuinely free —
// that reserved space (and further out) is exactly what a future Threat
// halo is expected to occupy. No threat-level prop/data is added here;
// see this phase's own final report for the recommended future halo
// radii — deliberately not pre-built (§10: "prefer not to pre-build
// unused feature code").
const SELECTION_RING_GAP = 1.5;
// Phase B.4a.1 — exported (was module-private) so the Threat ring
// overlay (TeamPlaybookPlayEditor.js's own ThreatHalo) can size itself
// directly off this SAME concentric geometry ("selected ring radius + a
// small offset") rather than an independently-guessed magic radius —
// the exact reuse this phase's spec asks for. Purely an export-surface
// change; the value and every existing use of it here are unchanged.
export const SELECTION_RING_RADIUS = VISUAL_RADIUS + SELECTION_RING_GAP;

// Playbook Phase B.0 — a Play shows two sides on one field, so markers
// need to be visually distinguishable by side without going overboard
// on styling. `side` defaults to 'ours' (the original, unchanged green
// look every existing Formation Builder caller already gets since none
// of them pass this prop) — 'opponent' swaps in a warm amber/red palette
// instead. Purely a fill/stroke color choice, no size/shape/behavior
// difference.
const PALETTES = {
  ours: { fill: '#0a2a14', stroke: '#2ea84b', selectedFill: '#2ea84b', selectedStroke: '#e8f0f8', selectedText: '#050c16' },
  opponent: { fill: '#3a1408', stroke: '#e0703a', selectedFill: '#e0703a', selectedStroke: '#fff2e8', selectedText: '#2a0e02' },
};

export default function PlaybookMarker({ px, py, label, selected, side = 'ours', onPointerDown, onPointerMove, onPointerUp }) {
  const palette = PALETTES[side] || PALETTES.ours;
  return (
    <g transform={`translate(${px}, ${py})`}>
      {/* Invisible, larger hit target — owns all pointer interaction. */}
      <circle
        r={HIT_RADIUS}
        fill="transparent"
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        style={{ pointerEvents: 'all', cursor: onPointerDown ? 'grab' : 'default', touchAction: 'none' }}
      />
      {/* Compact visible position dot — purely decorative, ignores pointer events so the hit circle above always wins. */}
      <circle
        r={VISUAL_RADIUS}
        fill={selected ? palette.selectedFill : palette.fill}
        stroke={selected ? palette.selectedStroke : palette.stroke}
        strokeWidth={1.5}
        style={{ pointerEvents: 'none' }}
      />
      {/* Phase B.3d (§8/§9) — selection ring: a distinct outer circle
          rendered ONLY when selected, with a real gap from the token
          (see SELECTION_RING_GAP's own header note above). Purely
          decorative, same pointer-events-none pattern as the token
          itself. */}
      {selected && (
        <circle
          r={SELECTION_RING_RADIUS}
          fill="none"
          stroke="#e8f0f8"
          strokeWidth={1.25}
          style={{ pointerEvents: 'none' }}
        />
      )}
      {/* Phase B.3d (§6/§7) — fontSize reduced from 7.5 to 6.25 (a
          conservative ~83% of the old size, deliberately less aggressive
          than the token's own 75% shrink — "do not shrink text
          proportionally just because the circle shrank") so a 3-
          character label (HB1, MLB, OLB, ...) still fits cleanly inside
          the new, smaller VISUAL_RADIUS with a visible margin, verified
          against the new 13.5-unit diameter. */}
      <text
        textAnchor="middle" dominantBaseline="central"
        fontSize={6.25} fontWeight={800}
        fill={selected ? palette.selectedText : palette.stroke}
        style={{ userSelect: 'none', pointerEvents: 'none' }}
      >
        {(label || '?').slice(0, 3)}
      </text>
    </g>
  );
}
